import { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import type { UserRole } from '@ns-b2b/shared';

const DEMO_USERS = [
    { id: 'U_demo_admin', name: 'Admin', role: 'ADMIN' as UserRole },
    { id: 'U_demo_exec', name: 'Executive', role: 'EXECUTIVE' as UserRole },
    { id: 'U_demo_manager_a', name: 'Manager A', role: 'MANAGER' as UserRole },
    { id: 'U_demo_employee_a', name: 'Employee A', role: 'EMPLOYEE' as UserRole },
];

export function LoginGate() {
    const { login, isLoading } = useAuth();
    const [showDevLogin, setShowDevLogin] = useState(false);

    const handleLineLogin = () => {
        window.location.href = '/api/auth/line/oauth/authorize';
    };

    const handleDevLogin = async (userId: string, role: UserRole) => {
        await login(userId, role);
    };

    return (
        <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-4">
            <div className="w-full max-w-sm">
                {/* Logo */}
                <div className="text-center mb-10">
                    <div className="w-20 h-20 bg-primary rounded-2xl flex items-center justify-center mx-auto shadow-card">
                        <span className="text-white font-bold text-3xl">NS</span>
                    </div>
                    <h1 className="text-2xl font-bold text-neutral-900 mt-5">Small B2B</h1>
                    <p className="text-neutral-500 mt-1">ระบบบริหารงานขาย</p>
                </div>

                {/* Login Card */}
                <div className="card p-6">
                    {/* Steps */}
                    <div className="space-y-4 mb-8">
                        <Step number={1} title="เข้าสู่ระบบ" description="ใช้บัญชี LINE ของคุณ" active />
                        <Step number={2} title="รอการอนุมัติ" description="ผู้ดูแลระบบจะอนุมัติบัญชี" />
                        <Step number={3} title="เริ่มใช้งาน" description="บันทึกข้อมูลหน้างาน" />
                    </div>

                    {/* LINE Login Button */}
                    <button
                        onClick={handleLineLogin}
                        disabled={isLoading}
                        className="w-full bg-[#06C755] hover:bg-[#05B64D] text-white font-semibold 
                       py-3.5 px-4 rounded-xl flex items-center justify-center gap-3 
                       transition-all duration-200 shadow-soft hover:shadow-card
                       disabled:opacity-50 active:scale-[0.98]"
                    >
                        <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.627-.63h2.386c.349 0 .63.285.63.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.627-.63.349 0 .631.285.631.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.349 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.281.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314" />
                        </svg>
                        <span>เข้าสู่ระบบด้วย LINE</span>
                    </button>
                </div>

                {/* Dev Login (collapsible) */}
                <div className="mt-6">
                    <button
                        onClick={() => setShowDevLogin(!showDevLogin)}
                        className="text-xs text-neutral-400 hover:text-neutral-600 transition-colors w-full text-center"
                    >
                        {showDevLogin ? '▲ ซ่อน Dev Login' : '▼ Dev Login'}
                    </button>

                    {showDevLogin && (
                        <div className="mt-3 card p-4 space-y-2">
                            <p className="text-xs text-neutral-500 text-center mb-3">
                                สำหรับการพัฒนาเท่านั้น
                            </p>
                            <div className="grid grid-cols-2 gap-2">
                                {DEMO_USERS.map((user) => (
                                    <button
                                        key={user.id}
                                        onClick={() => handleDevLogin(user.id, user.role)}
                                        disabled={isLoading}
                                        className="btn-secondary text-xs py-2"
                                    >
                                        {user.name}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <p className="text-center text-xs text-neutral-400 mt-8">
                    © 2026 DoHome Public Company Limited
                </p>
            </div>
        </div>
    );
}

function Step({
    number,
    title,
    description,
    active = false
}: {
    number: number;
    title: string;
    description: string;
    active?: boolean;
}) {
    return (
        <div className="flex items-start gap-3">
            <div className={`
        w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-semibold
        ${active ? 'bg-primary text-white' : 'bg-neutral-200 text-neutral-500'}
      `}>
                {number}
            </div>
            <div>
                <p className={`font-medium ${active ? 'text-neutral-900' : 'text-neutral-600'}`}>
                    {title}
                </p>
                <p className="text-sm text-neutral-500">{description}</p>
            </div>
        </div>
    );
}
